// @ts-nocheck
import { useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { IconCheck, IconX } from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import Time from '@/utils/time';
import PageContext from '@/utils/page-context';
import CompanyService from '@/services/company-service';
import FeedbackService from '@/services/feedback-service';
import * as Enums from '@/utils/enums';
import styles from '@/PageComponents/CustomerZone/CustomerZoneDetail.module.css';

type View = 'loading' | 'rate' | 'thanks' | 'used';

const STARS = [1, 2, 3, 4, 5];

function StarRating({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {STARS.map((n) => (
        <button
          key={n}
          type="button"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            color: n <= (hovered || value) ? '#f59e0b' : '#d1d5db',
            fontSize: 32, lineHeight: 1, transition: 'color 0.1s',
          }}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          aria-label={`${n} star${n !== 1 ? 's' : ''}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function JobFeedback() {
  const router = useRouter();
  const { t, c, i: jobID, r: defaultRatingParam, cc: contactID } = router.query;
  const pageContext = useContext(PageContext) as any;

  const [view, setView] = useState<View>('loading');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackID, setFeedbackID] = useState<string | null>(null);

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: company } = useQuery(
    ['czCompany', t, c],
    () => CompanyService.getCustomerZoneCompany(t, c, tenant.API),
    { enabled: !!tenant }
  );

  const { data: jobData } = useQuery(
    ['czFeedbackJob', t, c, jobID],
    () => Fetch.get({ url: '/CustomerZone/GetJob', params: { jobCardID: jobID }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!jobID }
  );

  useEffect(() => {
    if (!jobData || !tenant) return;
    if (jobData.headerTenantName) pageContext?.updateTenantName?.(jobData.headerTenantName);
    FeedbackService.getFeedbackByItemIDCustomerZone(jobData.ID, t, c, tenant.API).then((feedback: any) => {
      if (!feedback || feedback?.Rating) {
        setView('used');
      } else {
        setFeedbackID(feedback.ID);
        const parsed = parseInt(defaultRatingParam as string, 10);
        if (parsed >= 1 && parsed <= 5) setRating(parsed);
        setView('rate');
      }
    });
  }, [jobData, tenant]);

  if (!tenant || !jobData) return null;

  const job = jobData;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';
  const base = `?t=${t}&c=${c}`;

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      await FeedbackService.saveFeedbackCustomerZone(
        {
          ID: feedbackID,
          ItemID: job.ID,
          Module: Enums.Module.JobCard,
          Rating: rating,
          Comment: comment,
          CompletedDate: Time.today(),
          CustomerContactID: contactID || null,
        },
        t, c, tenant.API
      );
      setView('thanks');
    } catch {
      // stay on rate view; user can retry
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>

      <div className={styles.page}>
        <div className={styles.wrap}>
          <div className={styles.portalId}>
            <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} />
              ) : (
                <span className={styles.portalBadgeFallback}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                  </svg>
                </span>
              )}
            </div>
            <p className={styles.portalName}>{companyName}</p>
          </div>

          {view === 'rate' && (
            <div className={styles.detail}>
              <h1 className={`${styles.dId} ${styles.signCenterTitle}`}>Rate your experience</h1>
              <div className={styles.signCard}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                  How would you rate the service for {job.JobCardNumber}?
                </p>
                <StarRating value={rating} onChange={setRating} />
                <div className={styles.signField} style={{ alignSelf: 'stretch' }}>
                  <label className={styles.signLabel} htmlFor="fb-comment">Comment (optional)</label>
                  <textarea
                    id="fb-comment"
                    className={styles.signInput}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <button
                  className={`${styles.act} ${styles.actPrimary} ${styles.signSubmit}`}
                  type="button"
                  disabled={!rating || submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </div>
          )}

          {view === 'thanks' && (
            <div className={styles.detail}>
              <h1 className={`${styles.dId} ${styles.signCenterTitle}`}>Thank you!</h1>
              <div className={styles.signCard}>
                <div className={`${styles.signGlyph} ${styles.signGlyphOk}`}>
                  <IconCheck size={24} strokeWidth={2.5} />
                </div>
                <span className={styles.signSuccessT}>Feedback submitted</span>
                <p className={styles.signSuccessP}>
                  Thank you for rating {job.JobCardNumber}. Your feedback helps us improve our service.
                </p>
                <a className={`${styles.act} ${styles.actPrimary}`} href={`/customerzone${base}`}>
                  Back to home
                </a>
              </div>
            </div>
          )}

          {view === 'used' && (
            <div className={styles.detail}>
              <h1 className={`${styles.dId} ${styles.signCenterTitle}`}>Link unavailable</h1>
              <div className={styles.signCard}>
                <div className={`${styles.signGlyph}`} style={{ background: '#fef3c7', color: '#d97706' }}>
                  <IconX size={22} strokeWidth={2} />
                </div>
                <span className={styles.signSuccessT}>This feedback link has already been used</span>
                <p className={styles.signSuccessP}>
                  Feedback for {job.JobCardNumber} has already been submitted. Thank you for your response.
                </p>
                <a className={`${styles.act}`} href={`/customerzone${base}`}>
                  Back to home
                </a>
              </div>
            </div>
          )}

          <div className={styles.support}>
            <span className={styles.sTitle}>Need support?</span>
            <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobFeedback;
export const Route = createFileRoute('/_customerzoneLayout/customerzone/jobfeedback')({ component: JobFeedback });
